/* eslint-disable @typescript-eslint/member-delimiter-style */
import classnames from 'classnames'
import {
  Component,
  AnyComponent,
  ComponentChildren,
  FunctionalComponent,
  ComponentChild
} from 'preact'
import TextButton from '../../../control-components/ui-components/TextButton'
import { getFirstChild, isFunction } from '../../../libs/typeGuards'
import GlobalClick from '../../interactive/documentClick'

interface Props {
  popupName: string
  triggerElement?: AnyComponent<{ onClick: (e: Event) => void; disabled?: boolean }> | string
  trigger?: ComponentChildren | ((isTriggerOpen: boolean) => ComponentChildren)
  children: ComponentChild
  closeOnDropdownClick?: boolean
  disabled?: boolean
  isControl?: boolean
  openAlwaysOnTriggerClick?: boolean
  size?: 'S' | 'M' | 'L'
  position?: 'top' | 'bottom' | 'right' | 'left'
  align?: 'top' | 'bottom' | 'right' | 'left'
}

interface State {
  isOpen: boolean
  isTriggerClicked: boolean
  overflow: boolean
}

interface DropdownItemProps {
  children: ComponentChildren
  onClick?: () => void
  type?: 'normal' | 'alert' | 'info'
}

export const DropdownItem: FunctionalComponent<DropdownItemProps> = ({
  children,
  type = 'normal',
  onClick = () => {}
}: DropdownItemProps) => (
  <li className={`c-dropdown__item c-dropdown__item-type-${type}`} onClick={() => onClick()}>
    {children}
  </li>
)

export const DropdownItemNestedDropdown = (props: {
  children: ComponentChildren
  key?: string
}) => <li className='c-dropdown--nested'>{props.children}</li>

export const DropdownDivider = () => <li aria-hidden className='c-dropdown--divider' />

export default class extends Component<Props, State> {
  private dropdownContent: any | null = null
  private dropdownWrapper: HTMLInputElement | null = null
  private readonly TOP = 'top'
  private readonly BOTTOM = 'bottom'
  private readonly RIGHT = 'right'
  private readonly LEFT = 'left'

  constructor(props: Props) {
    super(props)
    this.state = {
      isOpen: false,
      isTriggerClicked: false,
      overflow: false
    }
  }

  componentDidUpdate(previousProps: Readonly<Props>, previousState: Readonly<State>) {
    const contentDimensions = this.dropdownContent?.base?.getBoundingClientRect()
    const wrapperDimensions = this.dropdownWrapper?.getBoundingClientRect()

    if (!previousState.isOpen && this.state.isOpen && wrapperDimensions && contentDimensions) {
      if (
        this.props.position === this.RIGHT ||
        this.props.position === this.TOP ||
        this.props.position === this.BOTTOM
      ) {
        this.setState({
          overflow:
            wrapperDimensions.right + parseInt(contentDimensions.width) >
            window.document.body.offsetWidth
        })
      } else if (this.props.position === this.LEFT) {
        this.setState({ overflow: wrapperDimensions.left < contentDimensions.width })
      }
    }
  }

  render({
    trigger,
    children,
    triggerElement,
    disabled,
    popupName,
    closeOnDropdownClick,
    openAlwaysOnTriggerClick = false,
    isControl,
    size = 'S',
    position = this.BOTTOM,
    align = this.LEFT
  }: Props) {
    let triggerComponent: ComponentChildren = null
    if (typeof trigger === 'function') {
      triggerComponent = trigger(this.state.isOpen)
    }
    const Trigger = triggerElement || TextButton
    const renderPropChildren = getFirstChild(children)
    return (
      <div
        className={classnames('c-dropdown', {
          'c-dropdown c-dropdown--app-control': isControl,
          'c-dropdown c-dropdown--app-destination': !isControl
        })}
        ref={(dropdownWrapper) => {
          this.dropdownWrapper = dropdownWrapper
        }}
      >
        <Trigger
          aria-haspopup={popupName}
          aria-expanded={this.state.isOpen}
          disabled={disabled}
          onClick={() => {
            if (disabled) {
              return
            }
            this.setState({
              isOpen: openAlwaysOnTriggerClick ? true : !this.state.isOpen,
              isTriggerClicked: true
            })
          }}
        >
          {triggerComponent ?? trigger}
        </Trigger>
        <GlobalClick
          Component='ul'
          tabIndex={-1}
          role={popupName}
          ref={(dropdownContent: any) => {
            this.dropdownContent = dropdownContent
          }}
          className={classnames(
            'c-dropdown__content',
            `c-dropdown__content-size-${size}`,
            `c-dropdown__content-position-${position}`,
            `c-dropdown__content-align-${align}`,
            {
              'is-hidden': !this.state.isOpen,
              'c-dropdown__content-overflow': this.state.overflow
            }
          )}
          onChildrenClick={() => {
            if (closeOnDropdownClick) {
              return this.setState({ isOpen: false, isTriggerClicked: false })
            }
            return ''
          }}
          onGlobalClick={() => {
            this.state.isTriggerClicked
              ? this.setState({ isTriggerClicked: false })
              : this.setState({ isOpen: false, isTriggerClicked: false })
          }}
        >
          {isFunction(renderPropChildren) &&
            renderPropChildren({
              close: () => this.setState({ isOpen: false, isTriggerClicked: false })
            })}
          {children}
        </GlobalClick>
      </div>
    )
  }
}
